import { ref, readonly } from 'vue'

// Cookie consent durumu
const consentStatus = ref(null) // 'accepted', 'rejected', null
const consentDate = ref(null)

// Consent durumunu localStorage'dan yükle
const loadConsent = () => {
  const consent = localStorage.getItem('cookie_consent')
  const date = localStorage.getItem('cookie_consent_date')
  
  consentStatus.value = consent
  consentDate.value = date
  
  return consent
}

// Çerezleri kabul et
const acceptCookies = () => {
  localStorage.setItem('cookie_consent', 'accepted')
  localStorage.setItem('cookie_consent_date', new Date().toISOString())
  consentStatus.value = 'accepted'
  consentDate.value = new Date().toISOString()
  
  // Analytics scriptlerini yükle
  loadAnalytics()
}

// Çerezleri reddet
const rejectCookies = () => {
  localStorage.setItem('cookie_consent', 'rejected')
  localStorage.setItem('cookie_consent_date', new Date().toISOString())
  consentStatus.value = 'rejected'
  consentDate.value = new Date().toISOString()
  
  // Mevcut tracking çerezlerini sil
  clearTrackingCookies()
  
  // Analytics scriptlerini kaldır
  removeAnalytics()
}

// Consent'i sıfırla (kullanıcı tercihini değiştirmek isterse)
const resetConsent = () => {
  localStorage.removeItem('cookie_consent')
  localStorage.removeItem('cookie_consent_date')
  consentStatus.value = null
  consentDate.value = null
}

// Tracking çerezlerini sil
const clearTrackingCookies = () => {
  // Google Analytics çerezleri
  const gaCookies = ['_ga', '_gid', '_gat', '_gac_', '__utma', '__utmb', '__utmc', '__utmz', '__utmv']
  
  // Facebook Pixel çerezleri
  const fbCookies = ['_fbp', '_fbc', 'fr']
  
  // Diğer yaygın tracking çerezleri
  const otherCookies = ['_gcl_au', '_hjid', '_hjSessionUser', '_hjSession']
  
  const allTrackingCookies = [...gaCookies, ...fbCookies, ...otherCookies]
  
  // Tüm tracking çerezlerini sil
  allTrackingCookies.forEach(cookieName => {
    // Ana domain için sil
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    
    // Alt domain için sil
    const domain = window.location.hostname
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`
    
    // Üst domain için sil (örn: .example.com)
    const parts = domain.split('.')
    if (parts.length > 1) {
      const topDomain = '.' + parts.slice(-2).join('.')
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${topDomain};`
    }
  })
  
  // console.log('[Cookie Consent] Tracking cookies cleared')
}

// Google Analytics yükle
const loadGoogleAnalytics = (measurementId) => {
  if (!measurementId || consentStatus.value !== 'accepted') {
    // console.log('[Cookie Consent] Google Analytics not loaded - consent not given')
    return
  }
  
  // Zaten yüklü mü kontrol et
  if (window.gtag) {
    // console.log('[Cookie Consent] Google Analytics already loaded')
    return
  }
  
  // gtag.js script'ini yükle
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  script.id = 'google-analytics-script'
  document.head.appendChild(script)
  
  // gtag fonksiyonunu tanımla
  window.dataLayer = window.dataLayer || []
  window.gtag = function() {
    window.dataLayer.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', measurementId, {
    anonymize_ip: true, // IP anonimleştirme (GDPR uyumlu)
    cookie_flags: 'SameSite=None;Secure'
  })
  
  // console.log('[Cookie Consent] Google Analytics loaded:', measurementId)
}

// Facebook Pixel yükle
const loadFacebookPixel = (pixelId) => {
  if (!pixelId || consentStatus.value !== 'accepted') {
    // console.log('[Cookie Consent] Facebook Pixel not loaded - consent not given')
    return
  }
  
  // Zaten yüklü mü kontrol et
  if (window.fbq) {
    // console.log('[Cookie Consent] Facebook Pixel already loaded')
    return
  }
  
  // Facebook Pixel script'ini yükle
  !function(f,b,e,v,n,t,s) {
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;t.id='facebook-pixel-script';s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)
  }(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
  
  window.fbq('init', pixelId)
  window.fbq('track', 'PageView')
  
  // console.log('[Cookie Consent] Facebook Pixel loaded:', pixelId)
}

// Tüm analytics scriptlerini yükle (settings store'dan ID'leri alır)
const loadAnalytics = async () => {
  // console.log('[Cookie Consent] Analytics loading triggered')
  
  try {
    // Settings store'dan analytics ID'lerini al
    const { useSettingsStore } = await import('@/store/settings')
    const settingsStore = useSettingsStore()
    
    // Settings yüklenmemişse bekle
    if (!settingsStore.settings?.google_analytics_id && !settingsStore.settings?.facebook_pixel_id) {
      await settingsStore.loadSettings()
    }
    
    const gaId = settingsStore.settings?.google_analytics_id
    const fbPixelId = settingsStore.settings?.facebook_pixel_id
    
    if (gaId) {
      loadGoogleAnalytics(gaId)
    }
    if (fbPixelId) {
      loadFacebookPixel(fbPixelId)
    }
  } catch (error) {
    // console.error('[Cookie Consent] Error loading analytics:', error)
  }
}

// Analytics scriptlerini kaldır
const removeAnalytics = () => {
  // Google Analytics script'ini kaldır
  const gaScript = document.getElementById('google-analytics-script')
  if (gaScript) {
    gaScript.remove()
  }
  
  // Facebook Pixel script'ini kaldır
  const fbScript = document.getElementById('facebook-pixel-script')
  if (fbScript) {
    fbScript.remove()
  }
  
  // Global değişkenleri temizle
  delete window.gtag
  delete window.dataLayer
  delete window.fbq
  delete window._fbq
  
  // console.log('[Cookie Consent] Analytics scripts removed')
}

// Consent verilmiş mi kontrol et
const hasConsent = () => {
  return consentStatus.value === 'accepted'
}

// Consent reddedilmiş mi kontrol et
const isRejected = () => {
  return consentStatus.value === 'rejected'
}

// Consent bekliyor mu (henüz karar verilmemiş)
const isPending = () => {
  return consentStatus.value === null
}

// Store'u dışa aktar
export const useCookieConsent = () => {
  return {
    // State (readonly)
    consentStatus: readonly(consentStatus),
    consentDate: readonly(consentDate),
    
    // Actions
    loadConsent,
    acceptCookies,
    rejectCookies,
    resetConsent,
    clearTrackingCookies,
    
    // Analytics loaders
    loadGoogleAnalytics,
    loadFacebookPixel,
    loadAnalytics,
    removeAnalytics,
    
    // Helpers
    hasConsent,
    isRejected,
    isPending
  }
}

export default useCookieConsent
